import { Delete } from '@mui/icons-material'
import { Box, Button, Grid, IconButton, TextField, Typography, Link as MuiLink, Tooltip } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { DataGrid } from '@mui/x-data-grid'
import LoadingSpinner from 'components/ui/LoadingSpinner'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { ClientRoute } from 'routes/client'
import { selectLeaderboard } from 'store/leaderboard/slice'
import { deleteUserChallenge, getChallengeLeaderboard } from 'store/leaderboard/thunks'
import { getUserChallenge, addUserToChallenge, removeUserFromChallenge } from 'store/userChallenge/thunks'
import DeleteChallengeDialog from './DeleteChallengeDialog'
import RemovePlayerDialog from './RemovePlayerDialog'
import { useAppDispatch } from 'store'
import { ChevronLeftIcon } from 'assets/icons/chevron-left'
import { useIntl } from 'react-intl'
import { sendErrorNotification, sendSuccessNotification } from 'utils/notifications'
import { ChallengeComputedState, getChallengeTypeName } from 'utils/challenges'
import UserChallengeStatusBadge from 'components/challenge/UserChallengeStatusBadge'
import { ChallengeTypeConfig } from 'api/types'
import { selectChallenge } from 'store/challenge/slice'

const useStyles = makeStyles(() => ({
  deleteChallenge: {
    color: 'white',
    width: '28px',
    backgroundColor: '#E63946',
  },
  challengeDetails: {
    color: '#D9D9D9',
    fontSize: 16,
    fontWeight: '500',
  },
  textInput: {
    border: '1px solid #D9D9D9',
    borderRadius: '34px',
    padding: '8px 12px',
    lineHeight: '24px',
    minWidth: '240px',
    fontSize: '16px',
  },
  challengeHeader: {
    display: 'flex',
    width: '100%',
    backgroundColor: '#32724C',
    borderRadius: '16px',
    padding: '16px 40px',
  },
  challengeInfoContainer: {
    display: 'flex',
    width: '100%',
    fontFamily: 'Roboto',
  },
  challengeInfo: {
    display: 'flex',
    flexWrap: 'wrap',
    width: '100%',
    alignContent: 'flex-start',
    padding: 20,
  },
  challengeImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  challengeImage: {
    width: '115px',
    height: '115px',
    borderRadius: '50%',
    border: '3px solid #fff',
    backgroundColor: '#fff',
  },
  challengeName: {
    fontSize: '30px',
    fontWeight: 600,
    color: '#fff',
    flexBasis: '100%',
  },
  button: {
    borderRadius: '8px',
    height: '42px',
    minWidth: '150px',
  },
  evenRow: {
    backgroundColor: '#F5F5F9',
  },
}))

const UserChallengeLeaderboard = () => {
  const dispatch = useAppDispatch()
  const classes = useStyles()
  const { id: challengeId } = useParams()
  const navigate = useNavigate()
  const { leaderboard, status: leaderboardStatus } = useSelector(selectLeaderboard)
  const { challenge: userChallenge, status: userChallengeStatus } = useSelector(selectChallenge)
  const [initialLeaderboardFetched, setInitialLeaderboardFetched] = useState(false)
  const [initialChallengeFetched, setInitialChallengeFetched] = useState(false)
  const [showDeleteChallengeDialog, setShowDeleteChallengeDialog] = useState(false)
  const [showRemovePlayerDialog, setShowRemovePlayerDialog] = useState(false)
  const [playerToRemove, setPlayerToRemove] = useState<string | null>(null)
  const [searchText, setSearchText] = useState('')
  const [userIdInput, setUserIdInput] = useState('')
  const intl = useIntl()
  const location = useLocation()

  useEffect(() => {
    if (challengeId && !initialLeaderboardFetched) {
      dispatch(getChallengeLeaderboard({ challengeId, params: { limit: 100 } }))
      setInitialLeaderboardFetched(true)
    }
  }, [challengeId, initialLeaderboardFetched])

  useEffect(() => {
    if (challengeId && !initialChallengeFetched) {
      dispatch(getUserChallenge(challengeId))
      setInitialChallengeFetched(true)
    }
  }, [challengeId, initialChallengeFetched])

  const columns = [
    { field: 'rank', headerName: 'Rank', flex: 1 },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      renderCell: (params) => (
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            maxWidth: '100%',
            overflow: 'hidden',
          }}
        >
          <Link
            style={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              maxWidth: '100%',
              overflow: 'hidden',
            }}
            to={ClientRoute.ADMIN_USER(params.row.id || 0)}
          >
            {params.row.name}
          </Link>
        </span>
      ),
    },
    {
      field: 'hcp',
      headerName: 'HCP',
      flex: 1,
      renderCell: (params) => (
        <Tooltip title="Current handicap">
          <span>{params.row.hcp}</span>
        </Tooltip>
      ),
    },
    { field: 'country', headerName: 'Country', flex: 1 },
    {
      field: 'score',
      headerName: getChallengeTypeName(userChallenge?.typeConfig?.type ?? 'CHALLENGE_TYPE_UNSPECIFIED', intl),
      flex: 1,
    },
    {
      field: 'removeUser',
      headerName: '',
      width: 100,
      renderCell: (params) => {
        return (
          <IconButton
            size="small"
            onClick={() => handleRemovePlayer(params.row.participantId)}
            sx={{ color: '#E63946' }}
          >
            <Delete />
          </IconButton>
        )
      },
    },
  ]

  const getParticipantHandicap = (handicap: number | undefined) => {
    if (typeof handicap !== 'number') {
      return '0.0'
    }
    if (handicap <= 0) {
      return Math.abs(handicap).toString()
    }

    return `+${handicap}`
  }

  const rows = useMemo(() => {
    if (!leaderboard.length) {
      return []
    }
    return leaderboard.map((entry) => ({
      participantId: entry.participantId,
      id: entry.user?.userId ?? '-1',
      rank: entry.rank,
      name: `${entry.user?.firstName || ''} ${entry.user?.lastName || ''}`,
      hcp: getParticipantHandicap(entry.user?.hcp),
      country: entry.user?.country || '',
      score: entry.score,
      rounds: '-',
      removeUser: '',
    }))
  }, [leaderboard])

  const filteredRows = useMemo(() => {
    if (!searchText.trim() || searchText.trim().length < 3) {
      return rows
    }
    const searchLower = searchText.toLowerCase()
    return rows.filter((row) => row.name.toLowerCase().includes(searchLower))
  }, [rows, searchText])

  const handleAddPlayer = () => {
    if (userChallenge.challengeId && userIdInput) {
      dispatch(
        addUserToChallenge({
          challengeId: userChallenge.challengeId,
          userIds: [userIdInput],
        }),
      ).then(() => {
        setUserIdInput('')
      })
    }
  }

  const handleRemovePlayer = (participantId: string | null) => {
    setPlayerToRemove(participantId)
    setShowRemovePlayerDialog(true)
  }

  const handleConfirmRemovePlayer = () => {
    if (userChallenge.challengeId && playerToRemove) {
      dispatch(
        removeUserFromChallenge({
          challengeId: userChallenge.challengeId,
          participantId: playerToRemove,
        }),
      )
        .unwrap()
        .then(() => {
          dispatch(getChallengeLeaderboard({ challengeId: String(userChallenge.challengeId), params: { limit: 100 } }))
          setShowRemovePlayerDialog(false)
          setPlayerToRemove(null)
          sendSuccessNotification('User removed')
        })
        .catch((error) => {
          console.error(error)
          sendErrorNotification('Failed to remove user')
          setPlayerToRemove(null)
          setShowRemovePlayerDialog(false)
        })
    }
  }

  const handleShowSettings = () =>
    navigate(ClientRoute.ADMIN_CHALLENGE_VIEW_SETTINGS(String(userChallenge.challengeId)))

  const handleShowDeleteChallengeDialog = () => setShowDeleteChallengeDialog(true)

  const handleConfirmDeleteChallenge = () => {
    dispatch(deleteUserChallenge({ challengeId: String(userChallenge.challengeId) }))
      .unwrap()
      .then(() => {
        setShowDeleteChallengeDialog(false)
        if (location.state?.from) {
          sendSuccessNotification('Challenge deleted')
          navigate(location.state.from, {
            state: { selectedChallengeType: location.state.type },
          })
        } else {
          sendSuccessNotification('Challenge deleted')
          navigate(ClientRoute.ADMIN_CHALLENGES)
        }
      })
      .catch((error) => {
        console.error('Error deleting the challenge:', error)
        sendErrorNotification('failed to delete challenge')
        setShowDeleteChallengeDialog(false)
      })
  }

  if (userChallengeStatus === 'loading') {
    return <LoadingSpinner />
  }

  const getTypeLogo = (userChallenge: Challenge) => {
    switch (userChallenge.typeConfig?.type) {
      case ChallengeTypeConfig.BIRDIES_OR_BETTER:
        return '/images/userChallenges/icon_challenge_birdie.png'
      case ChallengeTypeConfig.NUMBER_OF_COURSES_PLAYED:
        return '/images/userChallenges/icon_challenge_course.png'
      case ChallengeTypeConfig.NUMBER_OF_HOLES_PLAYED:
        return '/images/userChallenges/icon_challenge_hole.png'
      case ChallengeTypeConfig.NUMBER_OF_PARS:
        return '/images/userChallenges/icon_challenge_par.png'
      case ChallengeTypeConfig.STABLEFORD:
        return '/images/userChallenges/icon_challenge_stableford.png'
      case ChallengeTypeConfig.STROKEPLAY:
        return '/images/userChallenges/icon_challenge_strokeplay.png'
      default:
        return ''
    }
  }

  return (
    <>
      <Grid container gap={3} sx={{ padding: 3 }}>
        {location.state?.from && (
          <Box gap={24}>
            <MuiLink
              style={{
                color: '#0BB163',
                gap: '8px',
                display: 'inline-flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() =>
                navigate(location.state.from, {
                  state: { selectedChallengeType: location.state.type },
                })
              }
            >
              <ChevronLeftIcon width="20px" height="20px" circleFill="#0BB163" pathStroke="#EDEFF1" />
              {intl.formatMessage({ id: 'strings.backToChallenges' })}
            </MuiLink>
          </Box>
        )}
        <div className={classes.challengeHeader}>
          <div className={classes.challengeInfoContainer} style={{ gap: '8px' }}>
            <div className={classes.challengeImageContainer}>
              <img className={classes.challengeImage} src={getTypeLogo(userChallenge)} alt="Challenge" />
            </div>
            <div className={classes.challengeInfo}>
              <Typography className={classes.challengeName} component="div">
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    maxWidth: '100%',
                    verticalAlign: 'middle',
                  }}
                >
                  {userChallenge?.info?.name}

                  <span
                    style={{
                      flexShrink: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <UserChallengeStatusBadge challengeComputedState={userChallenge.computedState} />
                  </span>
                </span>
              </Typography>
              <div>
                <Typography
                  className={classes.challengeDetails}
                >{`Type: ${getChallengeTypeName(userChallenge?.typeConfig?.type, intl)}`}</Typography>
                <Typography
                  className={classes.challengeDetails}
                >{`Challenge target: ${userChallenge?.typeConfig?.target ?? '-'}`}</Typography>
                {userChallenge.computedState === ChallengeComputedState.LIVE ? (
                  <Typography className={classes.challengeDetails}>{`Duration: ${new Date(
                    String(userChallenge?.schedule?.startAt),
                  ).toLocaleDateString()} -
                  ${new Date(String(userChallenge.schedule?.endAt)).toLocaleDateString()},
                  ${Math.ceil(
                    (new Date(String(userChallenge.schedule?.endAt)).getTime() - new Date().getTime()) /
                      (1000 * 60 * 60 * 24),
                    // ms to days
                  )} days remaining`}</Typography>
                ) : (
                  <Typography className={classes.challengeDetails}>
                    {`Duration: ${new Date(String(userChallenge.schedule?.startAt)).toLocaleDateString()} -
                  ${new Date(String(userChallenge.schedule?.endAt)).toLocaleDateString()}`}
                  </Typography>
                )}
              </div>
            </div>
          </div>
        </div>
        <Grid container sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <Grid item>
            <Grid container gap={1}>
              <Button variant="contained" className={classes.button} onClick={handleShowSettings}>
                Settings
              </Button>

              <IconButton
                size="medium"
                onClick={handleShowDeleteChallengeDialog}
                sx={{ backgroundColor: '#FF001F', borderRadius: '8px' }}
              >
                <Delete sx={{ color: 'white' }} />
              </IconButton>
            </Grid>
          </Grid>
          <Grid item>
            <Grid container gap={1}>
              <input
                className={classes.textInput}
                placeholder="User-ID"
                value={userIdInput}
                onChange={(e) => setUserIdInput(e.target.value.trim())}
              />
              <Button variant="contained" className={classes.button} onClick={handleAddPlayer}>
                Add player
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container sx={{ alignItems: 'center' }}>
            <Grid item>
              <Typography
                style={{
                  color: '#373D40',
                  fontSize: 35,
                  fontWeight: '600',
                }}
              >
                Leaderboard
              </Typography>
            </Grid>
            <Grid item xs display="flex" justifyContent="flex-end">
              <TextField
                placeholder="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '34px',
                    borderColor: '#D9D9D9',
                    fontSize: '16px',
                    minWidth: '240px',
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 12px',
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <DataGrid
            autoHeight
            rows={filteredRows}
            columns={columns}
            loading={leaderboardStatus === 'loading'}
            sx={{
              '& .MuiDataGrid-root, .MuiDataGrid-cell, .MuiDataGrid-columnHeader': {
                border: 'none',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
              '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
              '& .MuiDataGrid-topContainer': {
                borderTop: '0px solid var(--DataGrid-rowBorderColor)',
              },
            }}
            rowSelection={false}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? classes.evenRow : '')}
          />
        </Grid>
      </Grid>
      {showDeleteChallengeDialog && (
        <DeleteChallengeDialog
          open={showDeleteChallengeDialog}
          onClose={() => setShowDeleteChallengeDialog(false)}
          onDelete={handleConfirmDeleteChallenge}
        />
      )}
      {showRemovePlayerDialog && (
        <RemovePlayerDialog
          open={showRemovePlayerDialog}
          onClose={() => {
            setShowRemovePlayerDialog(false)
            setPlayerToRemove(null)
          }}
          onRemove={handleConfirmRemovePlayer}
        />
      )}
    </>
  )
}

export default UserChallengeLeaderboard
